import { Component, inject, signal } from '@angular/core';
import { BlogCreate, BlogDetails, BlogDraftCreate, BlogUpdate, TagEntity, ValueOfProduct } from '@repo/types';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ButtonClickEvent,
  CacBaseDialogComponent,
  formBuilder,
  CacDialogLayoutComponent,
  CacImageUploaderComponent,
  CacFormBuilderComponent,
  CacButtonComponent,
  APIHelper,
  CacFieldComponent,
} from '@controladad/ng-base';
import { formControl } from '@al00x/forms';
import {
  AttachmentApiService,
  BlogApiService,
  BlogCategoryApiService,
  FileType,
  TagApiService,
  UploadResult,
} from '@/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, filter, map, of, take, tap } from 'rxjs';
import { CommonModule } from '@angular/common';

export interface BlogDialogData {
  blog?: BlogDetails;
}

export type BlogDialogResult = boolean;

@Component({
  selector: 'feature-blog-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    CacDialogLayoutComponent,
    CacImageUploaderComponent,
    CacFormBuilderComponent,
    CacButtonComponent,
    CacImageUploaderComponent,
    CacFieldComponent,
  ],
  templateUrl: './blog-dialog.component.html',
  styleUrl: './blog-dialog.component.scss',
})
export class BlogDialogComponent extends CacBaseDialogComponent<BlogDialogData, BlogDialogResult> {
  private readonly blogApi = inject(BlogApiService);
  private readonly tagApi = inject(TagApiService);
  private readonly blogCategoryApi = inject(BlogCategoryApiService);
  private readonly attachmentApi = inject(AttachmentApiService);

  content = formControl<string>(null, Validators.required);
  image = formControl<string | UploadResult>(null, Validators.required);

  titleCharCount = signal<number>(0);
  subTitleCharCount = signal<number>(0);

  readonly titleMaxLength = 100;
  readonly subTitleMaxLength = 200;

  getAllTags() {
    return this.tagApi
      .getAll({
        filters: [
          {
            key: 'type',
            value: 1,
          },
        ],
      })
      .pipe(
        tap((tags) => {
          this.allTags$.next(tags);
          this.tagMap.clear();
          tags.forEach((tag) => {
            this.tagMap.set(tag.id, tag.name);
          });
        }),
      );
  }

  getAllBlogCategories() {
    return this.blogCategoryApi.getAllBlogCategories().pipe(
      tap((categories) => {
        this.allBlogCategories$.next(categories);
        this.categoryMap.clear();
        categories.forEach((category) => {
          this.categoryMap.set(category.id, category.name);
        });
      }),
    );
  }

  getCategoryName(categoryId: number): string {
    if (this.categoryMap.has(categoryId)) {
      return this.categoryMap.get(categoryId) || '';
    }
    return 'Loading...';
  }

  clearCategory(): void {
    this.form.controls.category.setValue(null);
  }

  allTags$ = new BehaviorSubject<TagEntity[]>([]);
  allBlogCategories$ = new BehaviorSubject<any[]>([]);

  private categoryMap = new Map<number, string>();

  titleAndSubTitleForm = formBuilder({
    cols: 1,
    inputs: {
      title: {
        control: formControl<string>(undefined, [Validators.required, Validators.maxLength(this.titleMaxLength)]),
        label: 'Title',
      },
      subTitle: {
        control: formControl<string>(undefined, [Validators.required, Validators.maxLength(this.subTitleMaxLength)]),
        label: 'Sub Title',
      },
    },
  });

  form = formBuilder({
    cols: 1,
    inputs: {
      slug: {
        control: formControl<string>(undefined, Validators.required),
        label: 'Slug',
      },
      status: {
        control: formControl<boolean>(true, Validators.required),
        label: 'Status',
        type: 'switch',
      },
      tags: {
        control: formControl<number[]>([]),
        type: 'select',
        label: 'Tags',
        multiple: true,
        optional: true,
        items: this.allTags$.pipe(map((tags) => tags.map((v) => ({ value: v.id, label: v.name })))),
      },
      category: {
        control: formControl<number | null>(null),
        type: 'select',
        label: 'Category',
        optional: true,
        items: this.allBlogCategories$.pipe(
          map((categories) => categories.map((v) => ({ value: v.id, label: v.name }))),
        ),
      },
    },
    onInit: () => {
      this.getAllTags().subscribe();
      this.getAllBlogCategories().subscribe();
    },
  });

  constructor() {
    super();
    this.form.controls.tags.value$.pipe(takeUntilDestroyed()).subscribe((tags) => {
      if (tags) {
        const stringTags = this.form.controls.tags.value.filter((tag) => typeof tag === 'string') as string[];
        const numericTags = this.form.controls.tags.value.filter((tag) => typeof tag === 'number') as number[];

        if (stringTags.length > 0) {
          const newTagIds: number[] = [];

          const createNextTag = (index: number) => {
            if (index >= stringTags.length) {
              this.form.controls.tags.setValue([...numericTags, ...newTagIds]);
              return;
            }

            this.tagApi.create({ name: stringTags[index], type: 1 }).subscribe(() => {
              this.getAllTags().subscribe((allTags) => {
                const createdTag = allTags.find((tag) => tag.name === stringTags[index]);
                if (createdTag) {
                  newTagIds.push(createdTag.id);
                }

                createNextTag(index + 1);
              });
            });
          };

          createNextTag(0);
        }
      }
    });

    this.titleAndSubTitleForm.controls.title.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      this.titleCharCount.set(value?.length || 0);
    });

    this.titleAndSubTitleForm.controls.subTitle.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      this.subTitleCharCount.set(value?.length || 0);
    });

    const id = this.data?.blog?.id;
    if (id) {
      this.blogApi
        .getBlogDetail(id)
        .pipe(takeUntilDestroyed())
        .subscribe((data) => {
          // find the category name and set it to the form
          this.form.patchValue({
            status: data.blog.status === 1,
            slug: data.blog.slug,
            tags: data.blog.tags?.map((tag) => tag.id) || [],
            category: data.blog.categoryId,
          });
          this.titleAndSubTitleForm.patchValue({
            title: data.blog.title,
            subTitle: data.blog.subTitle,
          });
          this.content.patchValue(data.blog.content);
          this.image.patchValue(data.blog.image.url);

          this.titleCharCount.set(data.blog.title?.length || 0);
          this.subTitleCharCount.set(data.blog.subTitle?.length || 0);
        });
    }
  }

  get options() {
    return {
      fileType: 'Blog' as FileType,
    };
  }

  onSubmit(e: ButtonClickEvent) {
    const values = this.form.getValue();
    const titleAndSubTitleValues = this.titleAndSubTitleForm.getValue();
    if (!values || !titleAndSubTitleValues) return;

    const id = this.data.blog?.id;
    const imagePath = this.getImagePath();

    const createModel: BlogCreate = {
      ...values,
      ...titleAndSubTitleValues,
      status: values.status ? 1 : 0,
      imagePath,
      content: this.content.value,
      tagIds: values.tags,
      categoryId: values.category,
    };

    const rawUpdateModel = {
      id: id!,
      imagePath: imagePath,
      status: values.status ? 1 : 0,
      title: titleAndSubTitleValues.title,
      subTitle: titleAndSubTitleValues.subTitle,
      slug: values.slug,
      content: this.content.value,
      tagIds: values.tags,
      categoryId: values.category,
    };

    const updateModel: BlogUpdate = APIHelper.nestValues(rawUpdateModel);

    (id ? this.blogApi.updateBlog(updateModel) : this.blogApi.create(createModel)).pipe(e.pipe()).subscribe(() => {
      this.submit(true);
    });
  }

  saveDraft() {
    const values = this.form.getValue();
    const titleAndSubTitleValues = this.titleAndSubTitleForm.getValue();
    if (!values || !titleAndSubTitleValues) return;

    const id = this.data.blog?.id;
    const imagePath = this.getImagePath();

    if (!id) {
      const createModel: BlogCreate = {
        ...values,
        ...titleAndSubTitleValues,
        status: values.status ? 1 : 0,
        imagePath,
        content: this.content.value,
        tagIds: values.tags,
      };

      this.blogApi.create(createModel).subscribe((result) => {
        if (result && result.id) {
          const draftModel: BlogDraftCreate = {
            id: result.id,
            title: titleAndSubTitleValues.title,
            subTitle: titleAndSubTitleValues.subTitle,
            slug: values.slug,
            status: values.status ? 1 : 0,
            imagePath,
            content: this.content.value,
            tagIds: values.tags,
          };

          this.blogApi.saveDraft(draftModel).subscribe(() => {
            this.submit(true);
          });
        } else {
          this.submit(true);
        }
      });
    } else {
      const draftModel: BlogDraftCreate = {
        id: id,
        title: titleAndSubTitleValues.title,
        subTitle: titleAndSubTitleValues.subTitle,
        slug: values.slug,
        status: values.status ? 1 : 0,
        imagePath,
        content: this.content.value,
        tagIds: values.tags,
      };

      this.blogApi.saveDraft(draftModel).subscribe(() => {
        this.submit(true);
      });
    }
  }

  restorePreviousVersion() {
    const id = this.data.blog?.id;
    if (!id) return;

    this.blogApi.deleteDraft(id).subscribe(() => {
      this.submit(true);
    });
  }
  getImagePath(): string {
    const imageValue = this.image.value;
    const imagePath = (typeof imageValue === 'string' ? this.data.blog?.image?.path : imageValue?.path) ?? null;
    return imagePath!;
  }

  convertFieldToValueForm<T>(value: T): ValueOfProduct<T> {
    return {
      value: value,
    };
  }

  openEditor() {
    dialog$
      .editor(this.content.value)
      .afterSubmit()
      .pipe(take(1))
      .subscribe((result) => {
        this.content.patchValue(result);
      });
  }

  private tagMap = new Map<number, string>();

  getTagName(tagId: number): string {
    if (this.tagMap.has(tagId)) {
      return this.tagMap.get(tagId) || '';
    }

    return 'Loading...';
  }

  removeTag(tagId: number): void {
    const currentTags = this.form.controls.tags.value || [];
    const updatedTags = currentTags.filter((id) => id !== tagId);
    this.form.controls.tags.setValue(updatedTags);
  }
}
